import { memo, useMemo } from 'react';
import Text from '@raydiant/raydial/components/Text';
import { DevicePlan, LicensePlan } from './types';

interface OrderDetailsItemProps {
  plan: LicensePlan | DevicePlan;
  type: 'license' | 'device';
  count: number;
  showBadge?: boolean;
}

interface PriceDistribution {
  priceDistributionText: string;
  priceTotal: string;
  promotionPrice: string;
}

const MONTHS_IN_YEAR = 12;

const formatCurrency = (currency: string, amount: number): string => {
  const currencyFormat = currency === 'USD' ? 'US$' : '';
  return `${currencyFormat}${amount.toFixed(2)}`;
};

const getPriceDistribution = (
  plan: LicensePlan | DevicePlan,
  count: number,
): PriceDistribution => {
  const { billingPeriod, price, currency, promotionPrice = 0 } = plan;
  const monthlyPrice =
    billingPeriod === 'annually' ? price / MONTHS_IN_YEAR : price;

  switch (billingPeriod) {
    case 'annually':
      return {
        promotionPrice: formatCurrency(currency, promotionPrice * count),
        priceDistributionText: `(${formatCurrency(
          currency,
          monthlyPrice,
        )}/month - Billed annually)`,
        priceTotal: `${formatCurrency(currency, price * count)}/year`,
      };
    case 'monthly':
      return {
        promotionPrice: formatCurrency(currency, promotionPrice * count),
        priceDistributionText: `(${formatCurrency(
          currency,
          monthlyPrice,
        )} each/month)`,
        priceTotal: `${formatCurrency(currency, price * count)}/month`,
      };
    default:
      return {
        promotionPrice: formatCurrency(currency, promotionPrice * count),
        priceDistributionText: 'One time payment',
        priceTotal:
          price > 0 ? formatCurrency(currency, price * count) : 'Free',
      };
  }
};

const getItemLabels = (type: 'license' | 'device') => {
  return type === 'license'
    ? { title: 'Total Licenses', subTitle: 'Displai License' }
    : { title: 'Devices', subTitle: 'Displai Player' };
};

const OrderDetailsItem = ({
  plan,
  type,
  count,
  showBadge = false,
}: OrderDetailsItemProps) => {
  const { title, subTitle } = useMemo(() => getItemLabels(type), [type]);

  const { priceDistributionText, priceTotal, promotionPrice } = useMemo(
    () => getPriceDistribution(plan, count),
    [plan, count],
  );

  return (
    <div className="flex px-6 justify-between items-end">
      <div className="flex flex-col gap-2">
        <Text className="text-gray-600">{title}</Text>
        <Text className="text-black" variant="smallSubheader">
          {count} x {subTitle}
        </Text>
      </div>
      <div className="flex flex-col gap-2 items-end flex-1 min-w-[182px]">
        <Text className="text-gray-600 text-end">{priceDistributionText}</Text>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {showBadge && plan?.badge && (
            <div className="bg-red-50 px-2 rounded-xl h-5 flex items-center justify-center">
              <Text className="text-red-800">
                {plan?.badge === '$199 Value'
                  ? `$${199 * count} Value`
                  : plan?.badge}
              </Text>
            </div>
          )}
          {plan?.showPromotionPrice ? (
            <div className="flex gap-2">
              <Text
                className="text-black line-through"
                variant="smallSubheader"
              >
                {priceTotal}
              </Text>
              <Text className="text-black" variant="smallSubheader">
                {promotionPrice}
              </Text>
            </div>
          ) : (
            <Text className="text-black" variant="smallSubheader">
              {priceTotal}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(OrderDetailsItem);
